import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import './Modal.css';

function Modal({ isOpen, onClose, title, children }) {
  useEffect(() => {
    if (isOpen) {
      // Simple approach: just prevent body scroll
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  // Extract emoji and text from title
  const extractEmojiAndText = (title) => {
    if (!title) return { emoji: null, text: '' };
    const emojiMatch = title.match(/^[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/u);
    if (emojiMatch) {
      const emoji = emojiMatch[0];
      const text = title.replace(emoji, '').trim();
      return { emoji, text };
    }
    return { emoji: null, text: title };
  };

  if (!isOpen) return null;

  const { emoji, text } = extractEmojiAndText(title);

  const modalContent = (
    <div 
      className="modal-overlay" 
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div 
        className="modal-content" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div className="modal-header-content">
            {emoji && (
              <div className="modal-header-icon-wrapper">
                {emoji}
              </div>
            )}
            <h2 id="modal-title">{text || title}</h2>
          </div>
          <button 
            className="modal-close" 
            onClick={onClose} 
            aria-label="Close modal"
            type="button"
          >
            ✕
          </button>
        </div>
        <div className="modal-body">
          {children}
        </div>
      </div>
    </div>
  );

  // Use React Portal to render at document body level
  return createPortal(modalContent, document.body);
}

export default Modal;
